import React, { useContext, useState, useRef, useEffect } from 'react'
import ProgressIndicator, {
  ProgressIndicatorInterface
} from '@veneer/core/dist/scripts/progress_indicator'
import { MfeComponentContext } from './types'
import * as S from './styles'
import * as lazyImport from './componentLoaderImports'
import { useSignupSession } from '../../hooks'
import { buildThemeSettings } from './themeSettings'

const getPlansComponent = (componentName: string) => {
  switch (componentName) {
    case 'ConsumerPlanSelector':
      return lazyImport.ConsumerPlanSelector
    case 'PlanSelectorV3':
      return lazyImport.PlanSelectorV3
    case 'PlanTypeSelector':
      return lazyImport.PlanTypeSelector
    default:
      return lazyImport.PlanSelector
  }
}

const getCountrySelector = (componentName: string) => {
  switch (componentName) {
    case 'CountrySelector':
      return lazyImport.CountrySelector
    case 'GapHandler':
      return lazyImport.GapHandler
    case 'LanguageSelector':
      return lazyImport.LanguageSelector
  }

  throw new Error(`Component ${componentName} not found.`)
}

const getUILegos = (componentName: string) => {
  switch (componentName) {
    case 'FreeTerms':
      return lazyImport.FreeTerms
  }
  throw new Error(`Component ${componentName} not found.`)
}

export const ComponentLoader = () => {
  const [component, setComponent] = useState<React.ReactElement | null>(null)
  const [ready, setReady] = useState<boolean>(false)
  const componentRef = useRef(null)
  const { name, props } = useContext(MfeComponentContext)
  const { isHpx } = useSignupSession()
  const { componentName, loadingAppearance = 'circular' } = props || {}

  useEffect(() => {
    const loadComponent = () => {
      if (ready) return

      switch (name) {
        case 'react-billing':
          componentRef.current = lazyImport.BillingForm
          break
        case 'react-shipping':
          // do not re-render ShippingForm component on prop change
          componentRef.current = lazyImport.ShippingForm
          break
        case 'react-manual-printer-claim':
          // do not re-render MPC component on prop change
          setComponent(<lazyImport.ManualPrinterClaim {...props} />)
          break
        case 'react-instant-ink-plans':
          componentRef.current = getPlansComponent(componentName as string)
          break
        case 'react-instant-ink-ui-legos':
          componentRef.current = getUILegos(componentName as string)
          break
        case 'react-instant-ink-country-selector':
          try {
            componentRef.current = getCountrySelector(componentName as string)
          } catch (e) {
            setComponent(<div>Error: {e.message}</div>)
          }
          break
        case 'react-instant-ink-automatic-renewal-notice':
          componentRef.current = lazyImport.AutomaticRenewalNotice
          break
        case 'react-instant-ink-learn-more':
          componentRef.current = lazyImport.LearnMore
          break
        case 'react-instant-ink-deeplink':
          componentRef.current = lazyImport.DeeplinkWebEnrollment
          break
        case 'react-instant-ink-faq':
          componentRef.current = lazyImport.FaqViewer
          break
        case 'react-instant-ink-error-notice':
          componentRef.current = lazyImport.ErrorNotice
          break
        default:
          setComponent(<div>Invalid Component</div>)
          break
      }

      setReady(true)
    }

    loadComponent()
  }, [name, componentName, props, ready])

  if (ready) {
    if (component || componentRef.current) {
      const fallback =
        loadingAppearance !== 'none' ? (
          <S.LoadingContainer>
            <ProgressIndicator
              appearance={
                loadingAppearance as ProgressIndicatorInterface['appearance']
              }
            />
          </S.LoadingContainer>
        ) : null

      const LazyComponent = componentRef.current
      const updatedProps = {
        ...props,
        themeSettings: buildThemeSettings(
          isHpx,
          props?.themeSettings as Record<string, unknown> | undefined
        )
      }

      return (
        <React.Suspense fallback={fallback}>
          {component || <LazyComponent {...updatedProps} />}
        </React.Suspense>
      )
    }
  }

  return null
}
